import type {RoadmapTimeframe} from 'client';

interface ParsedTimeframe {
  half: 1 | 2;
  year: number;
}

const parseTimeframe = (timeframe: RoadmapTimeframe): ParsedTimeframe | null => {
  for (const value of [timeframe.key, timeframe.label, timeframe.source_value]) {
    const match = value.match(/h([12])[\s_-]*(?:fy)?(\d{2,4})/i);
    if (!match) continue;
    const shortYear = Number(match[2]);
    return {
      half: Number(match[1]) as 1 | 2,
      year: shortYear < 100 ? 2000 + shortYear : shortYear,
    };
  }
  return null;
};

const parseQuarter = (quarter: string): {quarter: number; year: number} | null => {
  const match = quarter.trim().match(/^(\d{4})[\s-]?q([1-4])$/i);
  if (!match) return null;
  return {year: Number(match[1]), quarter: Number(match[2])};
};

const quarterHalf = (quarter: number): 1 | 2 => (quarter <= 2 ? 1 : 2);

export const getQuartersForRoadmapTimeframes = (timeframes: RoadmapTimeframe[]): string[] => {
  const quarters = new Set<string>();
  timeframes.forEach((timeframe) => {
    const parsed = parseTimeframe(timeframe);
    if (!parsed) return;
    const firstQuarter = parsed.half === 1 ? 1 : 3;
    quarters.add(`${parsed.year} Q${firstQuarter}`);
    quarters.add(`${parsed.year} Q${firstQuarter + 1}`);
  });
  return [...quarters].sort((left, right) => {
    const leftQuarter = parseQuarter(left);
    const rightQuarter = parseQuarter(right);
    if (!leftQuarter || !rightQuarter) return left.localeCompare(right);
    return (
      leftQuarter.year * 4 + leftQuarter.quarter - (rightQuarter.year * 4 + rightQuarter.quarter)
    );
  });
};

export const findRoadmapTimeframeForQuarter = (
  timeframes: RoadmapTimeframe[],
  quarter: string | null
): RoadmapTimeframe | null => {
  if (!quarter) return null;
  const parsedQuarter = parseQuarter(quarter);
  if (!parsedQuarter) return null;
  return (
    timeframes.find((timeframe) => {
      const parsedTimeframe = parseTimeframe(timeframe);
      return (
        parsedTimeframe?.year === parsedQuarter.year &&
        parsedTimeframe.half === quarterHalf(parsedQuarter.quarter)
      );
    }) ?? null
  );
};

export const getRequestedOrCurrentRoadmapTimeframeKey = ({
  timeframes,
  requestedTimeframe,
  requestedQuarter,
  currentQuarter,
}: {
  timeframes: RoadmapTimeframe[];
  requestedTimeframe: string | null;
  requestedQuarter: string | null;
  currentQuarter: string;
}): string | null => {
  const normalizedRequestedTimeframe = requestedTimeframe?.trim().toLocaleLowerCase();
  const canonicalRequestedTimeframe = normalizedRequestedTimeframe
    ? timeframes.find(
        (timeframe) => timeframe.key.toLocaleLowerCase() === normalizedRequestedTimeframe
      )
    : null;

  return (
    findRoadmapTimeframeForQuarter(timeframes, requestedQuarter)?.key ??
    canonicalRequestedTimeframe?.key ??
    findRoadmapTimeframeForQuarter(timeframes, currentQuarter)?.key ??
    null
  );
};

export const getDefaultQuarterForRoadmapTimeframe = (
  timeframe: RoadmapTimeframe | null,
  availableQuarters: string[],
  currentQuarter: string
): string => {
  const matchingQuarters = timeframe
    ? availableQuarters.filter(
        (quarter) => findRoadmapTimeframeForQuarter([timeframe], quarter)?.key === timeframe.key
      )
    : availableQuarters;
  if (matchingQuarters.includes(currentQuarter)) return currentQuarter;
  return matchingQuarters[0] ?? availableQuarters[0] ?? currentQuarter;
};

export const getRoadmapRequestQuarter = ({
  timeframe,
  requestedQuarter,
  currentQuarter,
}: {
  timeframe: RoadmapTimeframe | null;
  requestedQuarter: string | null;
  currentQuarter: string;
}): string | null => {
  const availableQuarters = timeframe ? getQuartersForRoadmapTimeframes([timeframe]) : [];
  if (availableQuarters.length === 0) return null;
  if (requestedQuarter && availableQuarters.includes(requestedQuarter)) {
    return requestedQuarter;
  }
  return getDefaultQuarterForRoadmapTimeframe(timeframe, availableQuarters, currentQuarter);
};
